import { minutes, percent, type ISODateTime, type Minutes, type Percent, type TaskId } from '../domain/branded.js';
import type { Calendar } from '../domain/calendar.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';
import { resolveCalendar, workingMinutesBetween, type ResolvedCalendar } from './calendar-engine.js';

export type ExpectedProgressStatus =
  /** 狀態日還沒到任務開始日。 */
  | 'notStarted'
  /** 落後預期（variance < −1 個百分點）。 */
  | 'behind'
  /** 與預期相符（±1 個百分點內）。 */
  | 'onTrack'
  /** 超前預期（variance > +1 個百分點）。 */
  | 'ahead'
  | 'complete';

export interface ExpectedProgress {
  readonly taskId: TaskId;
  /** 依工作時間線性推算，狀態日當下「應該」完成的比例。 */
  readonly expectedPercent: Percent;
  readonly actualPercent: Percent;
  /** actual − expected，正值為超前。 */
  readonly variancePercent: number;
  readonly status: ExpectedProgressStatus;
  /** 落後的工作分鐘數（未落後時為 0）。 */
  readonly lagWorkingMinutes: Minutes;
}

/** 浮點雜訊容忍度（百分點）。 */
const TOLERANCE = 1;

interface CalendarCache {
  readonly calendarsById: ReadonlyMap<string, Calendar>;
  readonly resolved: Map<string, ResolvedCalendar>;
  readonly childrenByParentId: ReadonlyMap<string, readonly Task[]>;
}

function buildCache(project: Project): CalendarCache {
  const childrenByParentId = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const list = childrenByParentId.get(task.parentId as string) ?? [];
    list.push(task);
    childrenByParentId.set(task.parentId as string, list);
  }
  return {
    calendarsById: new Map(project.calendars.map((calendar) => [calendar.id as string, calendar])),
    resolved: new Map(),
    childrenByParentId,
  };
}

function calendarFor(project: Project, task: Task, cache: CalendarCache): ResolvedCalendar | null {
  const wanted = (task.calendarId ?? project.defaultCalendarId) as string;
  const cached = cache.resolved.get(wanted);
  if (cached !== undefined) return cached;
  const definition = cache.calendarsById.get(wanted) ?? cache.calendarsById.get(project.defaultCalendarId as string);
  if (definition === undefined) return null;
  const resolved = resolveCalendar(definition, cache.calendarsById);
  cache.resolved.set(wanted, resolved);
  return resolved;
}

/** 任務跨度內的工作分鐘數；沒有日曆時退回時鐘分鐘數。 */
function span(calendar: ResolvedCalendar | null, from: ISODateTime, to: ISODateTime): number {
  if (calendar === null) {
    return Math.max(0, (Date.parse(to as string) - Date.parse(from as string)) / 60_000);
  }
  return Math.max(0, workingMinutesBetween(calendar, from, to) as number);
}

interface ProgressPair {
  readonly expected: number;
  readonly actual: number;
  readonly totalWorkingMinutes: number;
}

function expectedFor(project: Project, task: Task, asOf: ISODateTime, cache: CalendarCache): ProgressPair {
  const children = cache.childrenByParentId.get(task.id as string) ?? [];
  if (children.length > 0) {
    // 摘要任務：預期與實際都用同一套子任務加權（工時全為 0 時退回工期加權，
    // 再退回簡單平均）。實際值刻意不讀 task.percentComplete —— 儲存值若還沒
    // roll up，摘要會被誤判成落後。
    const rows = children.map((child) => expectedFor(project, child, asOf, cache));
    let totalWork = 0;
    let totalDuration = 0;
    let weightedWorkExpected = 0;
    let weightedWorkActual = 0;
    let weightedDurationExpected = 0;
    let weightedDurationActual = 0;
    let sumExpected = 0;
    let sumActual = 0;
    let totalWorkingMinutes = 0;
    children.forEach((child, index) => {
      const row = rows[index]!;
      totalWorkingMinutes += row.totalWorkingMinutes;
      const work = child.work as number;
      const duration = child.durationMinutes as number;
      totalWork += work;
      totalDuration += duration;
      weightedWorkExpected += work * row.expected;
      weightedWorkActual += work * row.actual;
      weightedDurationExpected += duration * row.expected;
      weightedDurationActual += duration * row.actual;
      sumExpected += row.expected;
      sumActual += row.actual;
    });
    const pick = (byWork: number, byDuration: number, bySum: number): number =>
      totalWork > 0 ? byWork / totalWork
        : totalDuration > 0 ? byDuration / totalDuration
          : bySum / children.length;
    return {
      expected: pick(weightedWorkExpected, weightedDurationExpected, sumExpected),
      actual: pick(weightedWorkActual, weightedDurationActual, sumActual),
      totalWorkingMinutes,
    };
  }

  const actual = task.percentComplete as number;
  const calendar = calendarFor(project, task, cache);
  const total = span(calendar, task.start, task.finish);
  // 完成日的判斷必須在開始日之前 —— 里程碑的 start === finish，
  // 先判斷開始日會讓狀態日剛好落在里程碑上時回報 0% 而不是 100%。
  if (asOf >= task.finish) return { expected: 100, actual, totalWorkingMinutes: total };
  if (asOf <= task.start) return { expected: 0, actual, totalWorkingMinutes: total };
  if (total <= 0) return { expected: 100, actual, totalWorkingMinutes: total };
  const elapsed = span(calendar, task.start, asOf);
  return { expected: Math.min(100, elapsed / total * 100), actual, totalWorkingMinutes: total };
}

function evaluate(project: Project, task: Task, asOf: ISODateTime, cache: CalendarCache): ExpectedProgress {
  const { expected, actual, totalWorkingMinutes } = expectedFor(project, task, asOf, cache);
  const variance = actual - expected;

  let status: ExpectedProgressStatus;
  if (actual >= 100) status = 'complete';
  else if (asOf < task.start) status = 'notStarted';
  else if (variance < -TOLERANCE) status = 'behind';
  else if (variance > TOLERANCE) status = 'ahead';
  else status = 'onTrack';

  const lag = status === 'behind' ? totalWorkingMinutes * (expected - actual) / 100 : 0;
  return {
    taskId: task.id,
    expectedPercent: percent(expected),
    actualPercent: percent(actual),
    variancePercent: variance,
    status,
    lagWorkingMinutes: minutes(Math.round(lag)),
  };
}

/**
 * 以任務的排程起迄與有效日曆的工作時間，線性推算狀態日當下的預期進度，
 * 並與實際 percentComplete 比較。摘要任務以子任務工時加權（工時全為 0 時改以工期加權）。
 * `asOf` 一律由呼叫端傳入（UI 用 `project.statusDate ?? 今天`），引擎不讀系統時鐘。
 */
export function computeExpectedProgress(project: Project, task: Task, asOf: ISODateTime): ExpectedProgress {
  return evaluate(project, task, asOf, buildCache(project));
}

export interface ProjectProgressSummary {
  readonly asOf: ISODateTime;
  /** 納入統計的葉節點任務數。 */
  readonly total: number;
  readonly counts: Readonly<Record<ExpectedProgressStatus, number>>;
  /** 落後任務的 id，供警示直接使用。 */
  readonly behindTaskIds: readonly TaskId[];
}

/**
 * 專案層級的進度落後盤點。只統計葉節點任務 —— 摘要任務的進度是子任務的加權
 * 結果，一併計入會讓同一份落後被重複計算。
 */
export function computeProjectProgressSummary(project: Project, asOf: ISODateTime): ProjectProgressSummary {
  const cache = buildCache(project);
  const counts: Record<ExpectedProgressStatus, number> = {
    notStarted: 0, behind: 0, onTrack: 0, ahead: 0, complete: 0,
  };
  const behindTaskIds: TaskId[] = [];
  let total = 0;

  for (const task of project.tasks) {
    if ((cache.childrenByParentId.get(task.id as string) ?? []).length > 0) continue;
    total += 1;
    const row = evaluate(project, task, asOf, cache);
    counts[row.status] += 1;
    if (row.status === 'behind') behindTaskIds.push(task.id);
  }
  return { asOf, total, counts, behindTaskIds };
}
